import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight image lightbox. No external deps.
 * Props: images = [{src, alt}], openIndex (null = closed), onClose
 */
const Lightbox = ({ images = [], openIndex, onClose }) => {
  const [current, setCurrent] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null && openIndex !== undefined) setCurrent(openIndex);
  }, [openIndex]);

  const prev = useCallback(
    () => setCurrent((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const next = useCallback(
    () => setCurrent((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (openIndex === null || openIndex === undefined) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIndex, onClose, prev, next]);

  if (openIndex === null || openIndex === undefined) return null;
  const img = images[current];

  return (
    <div
      className="fixed inset-0 z-[120] bg-dodgers/95 backdrop-blur-sm flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-6 right-6 w-11 h-11 inline-flex items-center justify-center border border-galactico/40 text-galactico hover:border-lime hover:text-lime transition-colors"
        aria-label="Chiudi"
        data-testid="lightbox-close"
      >
        <X size={20} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center border border-galactico/40 text-galactico hover:border-lime hover:text-lime transition-colors"
        aria-label="Precedente"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center border border-galactico/40 text-galactico hover:border-lime hover:text-lime transition-colors"
        aria-label="Successiva"
      >
        <ChevronRight size={22} />
      </button>
      <figure
        className="max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.src}
          alt={img.alt}
          className="max-w-full max-h-[80vh] object-contain"
          draggable="false"
        />
        <figcaption className="font-condensed uppercase tracking-widest text-xs text-galactico/70">
          {current + 1} / {images.length} — {img.alt}
        </figcaption>
      </figure>
    </div>
  );
};

export default Lightbox;
